// S10 — Pendientes de validar (supervisor), por rango / turno / línea / OP.
//
// Lista TODOS los bloques de producción sin validar del scope del supervisor y
// permite validarlos desde la lista (uno a uno o "todos los visibles"). Marca
// qué bloques RETIENEN la facturación de una OP terminada (DN4: una OP
// terminada con ≥ 1 bloque sin validar no entra al corte de facturación).
//
// Los filtros viven en la URL (?desde&hasta&turno&linea) para que los links
// desde S1 y los reloads conserven el contexto.
//
// Mobile + PC: la tabla va con scroll horizontal dentro de su contenedor.

import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { SupervisorPendienteBloque } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { KpiCard } from '../../components/ui/kpi-card';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { SupervisorSidebar } from '../../components/layout/SupervisorSidebar';
import { useAuth } from '../../lib/auth';
import { ApiError } from '../../lib/api/client';
import { useSupervisorPendientes, useValidarRegistro } from '../../lib/api/queries';
import { cn } from '../../lib/utils';

const fmt = (n: number) => n.toLocaleString('es-CO', { maximumFractionDigits: 0 });

function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** Resta `dias` a una fecha YYYY-MM-DD (aritmética en UTC para evitar DST). */
function restarDias(iso: string, dias: number): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d - dias)).toISOString().slice(0, 10);
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

export function S10PendientesPage() {
  const { state } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const hoy = hoyISO();
  const desdeParam = searchParams.get('desde');
  const hastaParam = searchParams.get('hasta');
  const turnoParam = Number(searchParams.get('turno'));
  const lineaParam = searchParams.get('linea');

  const desde = desdeParam && ISO_RE.test(desdeParam) ? desdeParam : hoy;
  const hasta = hastaParam && ISO_RE.test(hastaParam) ? hastaParam : hoy;
  const turnoSel =
    Number.isInteger(turnoParam) && turnoParam >= 1 && turnoParam <= 9 ? turnoParam : null;
  const lineaSel = lineaParam && lineaParam.length > 0 ? lineaParam : null;

  const setFiltro = (
    patch: Partial<Record<'desde' | 'hasta' | 'turno' | 'linea', string | null>>,
  ) => {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '') next.delete(k);
      else next.set(k, v);
    }
    setSearchParams(next, { replace: true });
  };

  const { data, isLoading, isError, error, isFetching } = useSupervisorPendientes({
    desde,
    hasta,
    turnoNumero: turnoSel,
    lineaId: lineaSel,
  });
  const validarMut = useValidarRegistro();
  const [validandoId, setValidandoId] = useState<string | null>(null);
  const [confirmarTodos, setConfirmarTodos] = useState(false);
  const [validandoTodos, setValidandoTodos] = useState(false);

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  const bloques = data?.bloques ?? [];
  const ops = data?.ops ?? [];
  const resumen = data?.resumen;
  const opsRetenidas = ops.filter((o) => o.terminada);
  // Arrastre: lo pendiente FUERA del rango en pantalla. Con el filtro en "Hoy"
  // el KPI puede decir 0 OP retenidas y leerse como que no hay ninguna, cuando
  // el dinero frenado está en meses anteriores.
  const arrastre = data?.arrastre;
  const fueraBloques = Math.max(0, (arrastre?.bloques ?? 0) - (resumen?.bloques ?? 0));
  const fueraOps = Math.max(0, (arrastre?.ops_retenidas ?? 0) - (resumen?.ops_retenidas ?? 0));
  const hayArrastreOculto = fueraBloques > 0 || fueraOps > 0;
  const verTodo = () => {
    if (arrastre?.desde) setFiltro({ desde: arrastre.desde, hasta: hoy });
  };

  // Agrupación por fecha (el backend ya ordenó fecha → turno → línea → hora).
  const grupos: { fecha: string; items: SupervisorPendienteBloque[]; paquetes: number }[] = [];
  for (const b of bloques) {
    const g = grupos[grupos.length - 1];
    if (g && g.fecha === b.fecha) {
      g.items.push(b);
      g.paquetes += b.cantidad_procesada;
    } else {
      grupos.push({ fecha: b.fecha, items: [b], paquetes: b.cantidad_procesada });
    }
  }

  const validarUno = (id: string) => {
    if (validandoId || validandoTodos) return;
    setValidandoId(id);
    validarMut.mutate(id, {
      onSuccess: () => toast.success('Registro validado'),
      onError: (err) => {
        toast.error(err instanceof ApiError ? err.message : 'Error al validar');
      },
      onSettled: () => setValidandoId(null),
    });
  };

  const validarTodos = async () => {
    setConfirmarTodos(false);
    if (validandoTodos || bloques.length === 0) return;
    setValidandoTodos(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const b of bloques) {
        try {
          await validarMut.mutateAsync(b.id);
          ok += 1;
        } catch {
          fail += 1;
        }
      }
    } finally {
      setValidandoTodos(false);
    }
    if (ok > 0) toast.success(`${ok} ${ok === 1 ? 'bloque validado' : 'bloques validados'}`);
    if (fail > 0) toast.error(`${fail} no se ${fail === 1 ? 'pudo' : 'pudieron'} validar`);
  };

  const ocupado = validandoTodos || validandoId !== null;

  return (
    <RoleLayout sidebar={<SupervisorSidebar />} roleLabel="Supervisor">
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Pendientes de validar</h1>
            <p className="text-sm text-fg-tertiary">
              Capturas sin validar en sus plantas. Lo no validado no entra a la foto del turno ni al
              corte de facturación.
              {data?.plantas?.length ? ` · ${data.plantas.join(' · ')}` : ''}
            </p>
          </div>
          {isFetching ? <Spinner className="mb-2 size-4" /> : null}
        </header>

        {/* Filtros */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <Label htmlFor="s10-desde">Desde</Label>
            <Input
              id="s10-desde"
              type="date"
              value={desde}
              max={hasta}
              onChange={(e) => setFiltro({ desde: e.target.value })}
              className="w-40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="s10-hasta">Hasta</Label>
            <Input
              id="s10-hasta"
              type="date"
              value={hasta}
              min={desde}
              onChange={(e) => setFiltro({ hasta: e.target.value })}
              className="w-40"
            />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="s10-turno">Turno</Label>
            <Select
              id="s10-turno"
              value={turnoSel === null ? '' : String(turnoSel)}
              onChange={(e) => setFiltro({ turno: e.target.value || null })}
              className="w-56"
            >
              <option value="">Todos</option>
              {(data?.turnos ?? []).map((t) => (
                <option key={t.numero} value={t.numero}>
                  T{t.numero} · {t.nombre} {t.hora_inicio}–{t.hora_fin}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="s10-linea">Línea</Label>
            <Select
              id="s10-linea"
              value={lineaSel ?? ''}
              onChange={(e) => setFiltro({ linea: e.target.value || null })}
              className="w-56"
            >
              <option value="">Todas</option>
              {(data?.lineas ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.es_tienda ? `${l.nombre} (tienda)` : l.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant={desde === hoy && hasta === hoy ? 'primary' : 'outline'}
              onClick={() => setFiltro({ desde: hoy, hasta: hoy })}
            >
              Hoy
            </Button>
            <Button
              size="sm"
              variant={desde === restarDias(hoy, 6) && hasta === hoy ? 'primary' : 'outline'}
              onClick={() => setFiltro({ desde: restarDias(hoy, 6), hasta: hoy })}
            >
              Últimos 7 días
            </Button>
          </div>
        </div>

        {/* KPIs */}
        {resumen ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <KpiCard label="Bloques pendientes" value={fmt(resumen.bloques)} />
            <KpiCard label="Paquetes sin validar" value={fmt(resumen.paquetes)} />
            <KpiCard
              label="OPs retenidas"
              value={fmt(resumen.ops_retenidas)}
              hint="OP terminada con bloques sin validar: no entra al corte de facturación hasta validarlos"
              sub={
                resumen.ops_retenidas > 0
                  ? `Retienen facturación${fueraOps > 0 ? ` · ${fmt(fueraOps)} más fuera del rango` : ''}`
                  : fueraOps > 0
                    ? `Ninguna en este rango · ${fmt(fueraOps)} retenidas fuera`
                    : 'Ninguna retenida'
              }
              subTone={resumen.ops_retenidas > 0 || fueraOps > 0 ? 'warn' : 'neutral'}
            />
          </div>
        ) : null}

        {/* Arrastre fuera del rango: sin esto, "Hoy" esconde el dinero frenado */}
        {hayArrastreOculto && arrastre ? (
          <Card variant="warn-left">
            <CardContent className="flex flex-wrap items-center justify-between gap-3 p-3 pt-3 text-sm">
              <span className="text-fg-secondary">
                Fuera del rango que está viendo hay{' '}
                <strong className="text-warn">
                  {fmt(fueraBloques)} {fueraBloques === 1 ? 'bloque' : 'bloques'} sin validar
                </strong>
                {fueraOps > 0 ? (
                  <>
                    {' '}
                    y{' '}
                    <strong className="text-warn">
                      {fmt(fueraOps)} {fueraOps === 1 ? 'OP retenida' : 'OPs retenidas'}
                    </strong>
                  </>
                ) : null}
                {arrastre.desde ? ` desde el ${arrastre.desde}` : ''}. En total:{' '}
                {fmt(arrastre.bloques)} bloques · {fmt(arrastre.paquetes)} paq.
              </span>
              <Button size="sm" variant="secondary" onClick={verTodo} disabled={!arrastre.desde}>
                Ver todo lo pendiente
              </Button>
            </CardContent>
          </Card>
        ) : null}

        {/* OPs retenidas (DN4) */}
        {opsRetenidas.length > 0 ? (
          <Card variant="warn-left">
            <CardContent className="flex flex-col gap-1.5 p-3 pt-3 text-sm text-fg-secondary">
              <strong className="text-warn">
                {opsRetenidas.length === 1
                  ? '1 OP terminada no entra al corte de facturación'
                  : `${opsRetenidas.length} OPs terminadas no entran al corte de facturación`}
              </strong>
              <ul className="flex flex-col gap-0.5">
                {opsRetenidas.map((o) => (
                  <li key={`${o.numero_op}-${o.sku_codigo_cliente}`}>
                    OP <strong className="text-fg">{o.numero_op}</strong> · {o.sku_codigo_cliente} —{' '}
                    {o.bloques_pendientes} {o.bloques_pendientes === 1 ? 'bloque' : 'bloques'} sin
                    validar {o.bloques_pendientes === 1 ? 'retiene' : 'retienen'} la facturación (
                    {fmt(o.cantidad_capturada)} de{' '}
                    {o.cantidad_programada === null ? '—' : fmt(o.cantidad_programada)} paq
                    {o.cerrada_manual ? ', cierre manual' : ''})
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}

        {/* Acción masiva */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-fg-tertiary">
            Validar confirma que la captura es correcta. La acción queda registrada a su nombre.
          </p>
          <Button
            variant="primary"
            size="sm"
            disabled={bloques.length === 0 || ocupado}
            loading={validandoTodos}
            onClick={() => setConfirmarTodos(true)}
          >
            Validar todos los visibles ({bloques.length})
          </Button>
        </div>

        {/* Tabla agrupada por fecha */}
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-fg-tertiary">
            <Spinner className="size-5" /> Cargando pendientes…
          </div>
        ) : isError ? (
          <div className="rounded-md border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
            No pudimos cargar los pendientes. {error instanceof Error ? error.message : ''}
          </div>
        ) : bloques.length === 0 ? (
          <div className="rounded-md border border-border bg-surface-elevated px-4 py-8 text-center text-sm text-fg-tertiary">
            Nada pendiente en este rango. Todo lo capturado ya fue validado.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="bg-surface-elevated text-[11px] font-medium uppercase tracking-wider text-fg-tertiary">
                  <th className="px-3 py-2">Turno</th>
                  <th className="px-3 py-2">Hora</th>
                  <th className="px-3 py-2">Línea</th>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2 text-right">Paquetes</th>
                  <th className="px-3 py-2 text-right">Ops</th>
                  <th className="px-3 py-2">Capturó</th>
                  <th className="px-3 py-2">OP</th>
                  <th className="px-3 py-2 text-right">Acción</th>
                </tr>
              </thead>
              <tbody>
                {grupos.map((g) => (
                  <GrupoFecha
                    key={g.fecha}
                    grupo={g}
                    validandoId={validandoId}
                    ocupado={ocupado}
                    onValidar={validarUno}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmación de validación masiva */}
      <Dialog open={confirmarTodos} onClose={() => setConfirmarTodos(false)}>
        <DialogHeader>
          <DialogTitle>Validar todos los visibles</DialogTitle>
        </DialogHeader>
        <DialogContent>
          <p className="text-sm text-fg-secondary">
            Va a validar <strong className="text-fg">{bloques.length} bloques</strong> de{' '}
            <strong className="text-fg">
              {resumen?.dias ?? 0} {resumen?.dias === 1 ? 'día' : 'días'}
            </strong>
            . Esta acción queda registrada a su nombre.
          </p>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onClick={() => setConfirmarTodos(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={() => void validarTodos()}>
            Validar {bloques.length}
          </Button>
        </DialogFooter>
      </Dialog>
    </RoleLayout>
  );
}

// ---------------------------------------------------------------------------
// Sub-render: un grupo de filas por fecha (sub-header + bloques)
// ---------------------------------------------------------------------------

function GrupoFecha({
  grupo,
  validandoId,
  ocupado,
  onValidar,
}: {
  grupo: { fecha: string; items: SupervisorPendienteBloque[]; paquetes: number };
  validandoId: string | null;
  ocupado: boolean;
  onValidar: (id: string) => void;
}) {
  return (
    <>
      <tr className="border-t border-border bg-surface-overlay/60">
        <td colSpan={9} className="px-3 py-1.5 text-xs font-medium text-fg-secondary">
          {grupo.fecha} · {grupo.items.length} {grupo.items.length === 1 ? 'bloque' : 'bloques'} ·{' '}
          {fmt(grupo.paquetes)} paq
        </td>
      </tr>
      {grupo.items.map((b) => (
        <tr key={b.id} className="border-t border-border">
          <td className="whitespace-nowrap px-3 py-1.5 text-fg">
            T{b.turno_numero}
            <span className="ml-1 text-xs text-fg-tertiary">{b.turno_nombre}</span>
          </td>
          <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-fg">
            {b.hora_inicio}–{b.hora_fin}
          </td>
          <td className="whitespace-nowrap px-3 py-1.5 text-fg">
            {b.linea_nombre}
            {b.es_tienda ? <span className="ml-1 text-xs text-fg-tertiary">(tienda)</span> : null}
          </td>
          <td className="px-3 py-1.5">
            <div className="text-fg">{b.sku_codigo_cliente}</div>
            <div
              className="max-w-[16rem] truncate text-xs text-fg-tertiary"
              title={b.sku_descripcion}
            >
              {b.sku_descripcion}
            </div>
          </td>
          <td className="px-3 py-1.5 text-right tabular-nums text-fg">
            {fmt(b.cantidad_procesada)}
          </td>
          <td className="px-3 py-1.5 text-right tabular-nums text-fg">{b.numero_operarios}</td>
          <td className="whitespace-nowrap px-3 py-1.5 text-fg-secondary">
            {b.capturado_por_nombre ?? '—'}
            {b.capturado_por_supervisor ? (
              <span className="ml-1.5 rounded bg-surface-overlay px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-fg-tertiary">
                delegada
              </span>
            ) : null}
          </td>
          <td className="whitespace-nowrap px-3 py-1.5">
            {b.numero_op ? (
              <span className="inline-flex flex-wrap items-center gap-1.5">
                <span
                  className={cn('tabular-nums', b.retiene_facturacion ? 'text-warn' : 'text-fg')}
                >
                  {b.numero_op}
                </span>
                {b.retiene_facturacion ? (
                  <Badge tone="warn" dot>
                    Retiene facturación
                  </Badge>
                ) : null}
              </span>
            ) : (
              <span className="text-fg-tertiary">—</span>
            )}
          </td>
          <td className="px-3 py-1.5 text-right">
            <Button
              size="sm"
              variant="secondary"
              disabled={ocupado}
              loading={validandoId === b.id}
              onClick={() => onValidar(b.id)}
            >
              Validar
            </Button>
          </td>
        </tr>
      ))}
    </>
  );
}
